/* ============================================================
 * bundle-builder-products.js
 * Drives the Bundle Builder (Products) section. Forked from
 * bundle-builder.js (same UI, same interactions), with:
 *  - A min/max range instead of one fixed pack size: the CTA unlocks
 *    at data-min-selections; data-max-selections is the bundle size
 *    (slots, ring, top tier), and with data-allow-more customers can keep
 *    adding up to data-item-limit (counts past the size show as "6+")
 *  - Two discount tiers: live subtotal/discounted total in the
 *    summary and CTA, plus a "next tier" message
 *  - After the items are added (one real line item per product),
 *    the reached tier's discount code is applied to the cart via
 *    /cart/update.js, keeping any other codes already on the cart
 *  - Optional Skio subscribe & save: one-time vs subscription for the
 *    whole bundle, with an interval dropdown built from the products'
 *    Skio plans; each line item is added with its `selling_plan`
 *  - Boots on [data-bundle-builder-products], never
 *    [data-bundle-builder], so the two sections can share a page
 * ============================================================ */
(function () {
  'use strict';

  const PROGRESS_CIRCUMFERENCE = 2 * Math.PI * 28; // matches r=28 in the section's SVG

  const initializedRoots = new WeakSet();

  /**
   * Formats cents with the shop's own money format (passed in from
   * `shop.money_format`), covering the placeholders Shopify uses.
   */
  function formatMoney(cents, format) {
    const value = Math.round(Number(cents) || 0);
    const fmt = format || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', ',');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  /**
   * Summary thumbnails sit on the dark sidebar, and many packshots are
   * PNGs with a solid white background baked in (not transparent), which
   * shows as a white box. This makes a copy with that background removed:
   * a flood fill from the image's edges clears near-white pixels connected
   * to the border (so white INSIDE the product, like label text, is kept),
   * then softens the light fringe left along the cut. Images that are
   * already transparent at a corner are returned as-is, as is the
   * original URL on any failure (e.g. the image couldn't be read due to
   * CORS; Shopify's CDN allows it). Cached per URL.
   */
  const cutoutCache = new Map(); // url -> { promise, value }
  const CUTOUT_MAX_SIZE = 300;
  const CUTOUT_WHITE = 235; // min R, G and B to count as background

  function getCutout(url) {
    if (!url) return null;
    let cached = cutoutCache.get(url);
    if (!cached) {
      cached = { value: null, promise: null };
      cached.promise = stripWhiteBackground(url).then((result) => {
        cached.value = result;
        return result;
      });
      cutoutCache.set(url, cached);
    }
    return cached;
  }

  function stripWhiteBackground(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onerror = () => resolve(url);
      img.onload = () => {
        try {
          const scale = Math.min(1, CUTOUT_MAX_SIZE / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.max(1, Math.round(img.naturalWidth * scale));
          const h = Math.max(1, Math.round(img.naturalHeight * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, w, h);
          const imageData = ctx.getImageData(0, 0, w, h);
          const px = imageData.data;

          const corners = [0, w - 1, (h - 1) * w, h * w - 1];
          if (corners.some((p) => px[p * 4 + 3] < 250)) {
            resolve(url);
            return;
          }

          const isBackground = (p) => {
            const i = p * 4;
            return px[i + 3] > 0 && px[i] >= CUTOUT_WHITE && px[i + 1] >= CUTOUT_WHITE && px[i + 2] >= CUTOUT_WHITE;
          };
          const removed = new Uint8Array(w * h);
          const queued = new Uint8Array(w * h);
          const stack = [];
          const enqueue = (p) => {
            if (!queued[p]) {
              queued[p] = 1;
              stack.push(p);
            }
          };
          for (let x = 0; x < w; x++) {
            enqueue(x);
            enqueue((h - 1) * w + x);
          }
          for (let y = 0; y < h; y++) {
            enqueue(y * w);
            enqueue(y * w + w - 1);
          }
          while (stack.length) {
            const p = stack.pop();
            if (!isBackground(p)) continue;
            removed[p] = 1;
            px[p * 4 + 3] = 0;
            const x = p % w;
            if (x > 0) enqueue(p - 1);
            if (x < w - 1) enqueue(p + 1);
            if (p >= w) enqueue(p - w);
            if (p < w * (h - 1)) enqueue(p + w);
          }

          // Soften the edge: light pixels touching the removed area fade out
          // in proportion to how close to white they are.
          for (let p = 0; p < w * h; p++) {
            if (removed[p]) continue;
            const x = p % w;
            const touches =
              (x > 0 && removed[p - 1]) ||
              (x < w - 1 && removed[p + 1]) ||
              (p >= w && removed[p - w]) ||
              (p < w * (h - 1) && removed[p + w]);
            if (!touches) continue;
            const i = p * 4;
            const lightness = Math.min(px[i], px[i + 1], px[i + 2]);
            if (lightness > 190) {
              px[i + 3] = Math.round(px[i + 3] * Math.max(0, (CUTOUT_WHITE - lightness) / (CUTOUT_WHITE - 190)));
            }
          }

          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (e) {
          resolve(url);
        }
      };
      img.src = url;
    });
  }

  let instanceCount = 0;

  function init(root) {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);
    const instanceIndex = instanceCount++;

    const grid = root.querySelector('[data-bb-grid]');
    const tabsWrap = root.querySelector('[data-bb-tabs]');
    const cards = Array.from(root.querySelectorAll('[data-bb-card]'));
    // Slots can grow past the bundle size (see syncSlotCount), so these are
    // re-read whenever slots are added or removed.
    const slotsWrap = root.querySelector('[data-bb-slots]');
    let slots = Array.from(root.querySelectorAll('[data-bb-slot]'));
    const selectedList = root.querySelector('[data-bb-selected-list]');
    const countEl = root.querySelector('[data-bb-count]');
    const countLabelEl = root.querySelector('[data-bb-count-label]');
    const tierLabelEl = root.querySelector('[data-bb-tier-label]');
    const statusEl = root.querySelector('[data-bb-status]');
    const progressRing = root.querySelector('[data-bb-progress-ring]');
    const ctaBtn = root.querySelector('[data-bb-add-to-cart]');
    const ctaLabel = root.querySelector('[data-bb-cta-label]');
    const addStatusEl = root.querySelector('[data-bb-add-status]');
    const clearBtn = root.querySelector('[data-bb-clear]');

    // Mobile-only persistent bar (optional, null-guarded).
    const mobileDock = root.querySelector('[data-bb-mobile-dock]');
    const mobileToggle = root.querySelector('[data-bb-mobile-toggle]');
    const mobileChevron = root.querySelector('[data-bb-mobile-chevron]');
    const mobileCountEl = root.querySelector('[data-bb-mobile-count]');
    const mobileSummaryEl = root.querySelector('[data-bb-mobile-summary]');
    const mobileCountLabelEl = root.querySelector('[data-bb-mobile-count-label]');
    const mobileProgressRing = root.querySelector('[data-bb-mobile-progress-ring]');
    const mobileCtaBtn = root.querySelector('[data-bb-mobile-add-to-cart]');
    const mobileCtaLabel = root.querySelector('[data-bb-mobile-cta-label]');

    // Desktop-only floating summary (optional, null-guarded).
    const desktopToggle = root.querySelector('[data-bb-desktop-toggle]');
    const desktopChevron = root.querySelector('[data-bb-desktop-chevron]');
    const desktopPanel = root.querySelector('[data-bb-desktop-panel]');
    const desktopCountEl = root.querySelector('[data-bb-desktop-count]');
    const desktopCountLabelEl = root.querySelector('[data-bb-desktop-count-label]');
    const desktopTierLabelEl = root.querySelector('[data-bb-desktop-tier-label]');
    const desktopStatusEl = root.querySelector('[data-bb-desktop-status]');
    const desktopProgressRing = root.querySelector('[data-bb-desktop-progress-ring]');
    const desktopSlotsWrap = root.querySelector('[data-bb-desktop-slots]');
    let desktopSlots = Array.from(root.querySelectorAll('[data-bb-desktop-slot]'));
    const desktopSelectedList = root.querySelector('[data-bb-desktop-selected-list]');
    const desktopCtaBtn = root.querySelector('[data-bb-desktop-add-to-cart]');
    const desktopCtaLabel = root.querySelector('[data-bb-desktop-cta-label]');
    const desktopAddStatusEl = root.querySelector('[data-bb-desktop-add-status]');
    const desktopClearBtn = root.querySelector('[data-bb-desktop-clear]');
    const desktopToggleCountEl = root.querySelector('[data-bb-desktop-toggle-count]');
    const desktopFractionEl = root.querySelector('[data-bb-desktop-fraction]');
    const desktopToggleCountLabelEl = root.querySelector('[data-bb-desktop-toggle-count-label]');
    const desktopToggleRing = root.querySelector('[data-bb-desktop-toggle-ring]');

    // Tier legend + price summary. Rendered once in the sidebar and once in
    // the desktop panel with the same attributes, so every match is updated.
    const all = (selector) => Array.from(root.querySelectorAll(selector));
    const tierPills = all('[data-bb-tier-pill]');
    const subtotalRows = all('[data-bb-subtotal-row]');
    const subtotalEls = all('[data-bb-subtotal]');
    const discountRows = all('[data-bb-discount-row]');
    const discountPercentEls = all('[data-bb-discount-percent]');
    const discountAmountEls = all('[data-bb-discount-amount]');
    const comparePriceEls = all('[data-bb-compare-price]');
    const totalPriceEls = all('[data-bb-total-price]');
    const subscribeRows = all('[data-bb-subscribe-row]');
    const subscribeAmountEls = all('[data-bb-subscribe-amount]');
    const purchaseGroups = all('[data-bb-purchase]');
    const purchaseInputs = all('[data-bb-purchase-input]');
    const purchaseOptions = all('[data-bb-purchase-option]');
    const intervalSelects = all('[data-bb-interval]');
    const intervalWraps = all('[data-bb-interval-wrap]');
    const shippingEls = all('[data-bb-shipping]');
    const progressOfEls = all('.bundle-builder__progress-label-of');
    const shippingTextEls = all('[data-bb-shipping-text]');
    const shippingFillEls = all('[data-bb-shipping-fill]');

    if (!grid || !cards.length || !ctaBtn) return;

    const max = parseInt(root.dataset.maxSelections, 10) || 6;
    const min = Math.min(parseInt(root.dataset.minSelections, 10) || 3, max);
    // `max` is the bundle's target size (the slots, the ring, the top tier).
    // With "allow more" on, customers can keep adding past it up to
    // `itemLimit`; counts past `max` read as "6+" on the rings.
    const allowMore = root.dataset.allowMore === 'true';
    const itemLimit = allowMore ? Math.max(max, parseInt(root.dataset.itemLimit, 10) || max) : max;
    // Empty "bonus" slots shown past the bundle size, so it reads as
    // "6 or more". Once those fill, one more empty slot keeps appearing
    // after the last filled one, up to the item limit.
    const extraSlots = allowMore ? Math.max(0, parseInt(root.dataset.extraSlots, 10) || 0) : 0;
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const bundleLabel = root.dataset.bundleLabel || 'Bundle';
    const showCartTag = root.dataset.showCartTag !== 'false';

    // Skio subscribe & save. `purchase.mode` applies to the whole bundle; each
    // entry carries its own plans ({ "Every 30 Days": [planId, priceCents] })
    // from the configured Skio plan group.
    const subscriptionsEnabled = root.dataset.subscriptions === 'true';
    const tierOnSubscriptions = root.dataset.tierOnSubscriptions !== 'false';
    const onetimeOnlyLabel = root.dataset.labelOnetimeOnly || 'One-time only';
    // Free shipping progress: the bundle total plus what's already in the
    // cart (cart.total_price, fetched from /cart.js and refreshed on
    // cart:update), measured against the one-time or subscription threshold.
    const freeShipping = {
      enabled: root.dataset.freeShipping === 'true',
      threshold: parseInt(root.dataset.freeShippingThreshold, 10) || 0,
      subscriptionThreshold:
        parseInt(root.dataset.freeShippingSubscriptionThreshold, 10) ||
        parseInt(root.dataset.freeShippingThreshold, 10) ||
        0,
      cartTotal: 0,
    };
    const purchase = {
      mode: subscriptionsEnabled && root.dataset.defaultPurchase === 'subscribe' ? 'subscribe' : 'onetime',
      interval: '',
    };
    const cartType = root.dataset.cartType || 'drawer';

    /**
     * Discount tiers, sorted by quantity ascending. A tier with no
     * percent is dropped; a tier with no code still drives the price
     * display but applies nothing to the cart.
     */
    const tiers = [1, 2]
      // getAttribute, not `dataset`: a dash before a digit isn't camelCased,
      // so `data-tier-1-qty` is NOT `dataset.tier1Qty`.
      .map((n) => ({
        qty: parseInt(root.getAttribute('data-tier-' + n + '-qty'), 10) || 0,
        percent: parseFloat(root.getAttribute('data-tier-' + n + '-percent')) || 0,
        code: (root.getAttribute('data-tier-' + n + '-code') || '').trim(),
      }))
      // Only tiers within the bundle size, matching the tier boxes shown.
      .filter((tier) => tier.qty > 0 && tier.percent > 0 && tier.qty <= max)
      .sort((a, b) => a.qty - b.qty);
    const tierCodes = tiers.map((tier) => tier.code).filter(Boolean);

    const labels = {
      add: root.dataset.labelAdd || 'Add — %price%',
      addIncomplete: root.dataset.labelAddIncomplete || 'Select %remaining% More',
      adding: root.dataset.labelAdding || 'Adding…',
      added: root.dataset.labelAdded || 'Added ✓',
      error: root.dataset.labelError || 'Something went wrong — try again',
      statusReady: root.dataset.labelStatusReady || 'Ready',
      statusBuilding: root.dataset.labelStatusBuilding || 'Keep Going',
      countComplete: root.dataset.labelCountComplete || 'All %max% Selected',
      countProgress: root.dataset.labelCountProgress || '%count% of %max% Selected',
      countOver: root.dataset.labelCountOver || '%count% Selected',
      mobileSummaryOver: root.dataset.labelMobileSummaryOver || '%count% Items · %percent%% Off',
      tierNext: root.dataset.labelTierNext || 'Add %needed% more for %percent%% off',
      tierUnlocked: root.dataset.labelTierUnlocked || '%percent%% off unlocked',
      mobileSummary: root.dataset.labelMobileSummary || '%count%/%max% Items',
      mobileSummaryDiscount: root.dataset.labelMobileSummaryDiscount || '%count%/%max% · %percent%% Off',
      mobileTierNext: root.dataset.labelMobileTierNext || '+%needed% for %percent%% off',
      mobileTierUnlocked: root.dataset.labelMobileTierUnlocked || '%percent%% off unlocked',
      shippingAway: root.dataset.labelShippingAway || "You're %amount% away from FREE shipping",
      shippingUnlocked: root.dataset.labelShippingUnlocked || 'FREE shipping unlocked',
      mobileShippingAway: root.dataset.labelMobileShippingAway || '%amount% to free shipping',
      mobileShippingUnlocked: root.dataset.labelMobileShippingUnlocked || '%percent%% off + free shipping',
    };

    [progressRing, mobileProgressRing, desktopProgressRing, desktopToggleRing].forEach((ring) => {
      if (!ring) return;
      ring.style.strokeDasharray = String(PROGRESS_CIRCUMFERENCE);
      ring.style.strokeDashoffset = String(PROGRESS_CIRCUMFERENCE);
    });

    /** True while an add-to-cart request is in flight; blocks all edits. */
    let addToCartInFlight = false;

    /** Selection state: variantId -> entry */
    const state = new Map();
    /** One entry per selected unit, in chronological add order (see bundle-builder.js). */
    const unitOrder = [];
    /** Cancellable in-flight animation tokens, keyed by variant id. */
    const pendingFlightsById = new Map();

    function totalPending() {
      let total = 0;
      pendingFlightsById.forEach((list) => {
        total += list.length;
      });
      return total;
    }

    /**
     * One state entry per VARIANT. A card with a variant dropdown (only
     * in-stock variants are rendered as options) contributes one entry per
     * option; `card.dataset.variantId` always holds the currently selected
     * one, so the card's add/stepper act on that variant. Different
     * variants of the same product can be mixed, each its own line item.
     */
    function parsePlans(json) {
      if (!subscriptionsEnabled || !json) return {};
      try {
        const plans = JSON.parse(json);
        return plans && typeof plans === 'object' ? plans : {};
      } catch (e) {
        return {};
      }
    }

    const entriesByCard = new Map();
    cards.forEach((card) => {
      const select = card.querySelector('[data-bb-variant-select]');
      const sources = select
        ? Array.from(select.options).map((option) => ({
            id: option.value,
            variantTitle: option.dataset.title || '',
            price: option.dataset.price,
            color: option.dataset.color || card.dataset.color,
            image: option.dataset.image || card.dataset.image,
            plans: option.dataset.plans,
          }))
        : [
            {
              id: card.dataset.variantId,
              variantTitle: card.dataset.variantTitle || '',
              price: card.dataset.price,
              color: card.dataset.color,
              image: card.dataset.image,
              plans: card.dataset.plans,
            },
          ];
      const cardEntries = sources.map((source) => {
        const entry = {
          id: source.id,
          title: card.dataset.title || '',
          variantTitle: source.variantTitle,
          color: source.color || '',
          image: source.image || '',
          sourceImage: source.image || '',
          price: parseInt(source.price, 10) || 0,
          plans: parsePlans(source.plans),
          qty: 0,
          card,
        };
        state.set(entry.id, entry);
        return entry;
      });
      entriesByCard.set(card, cardEntries);
      if (select) card.dataset.variantId = select.value;
    });

    /**
     * Starts (or reuses) the background-removed thumbnail for an entry and
     * swaps it in once ready, repainting only the slots (not the CTA, so it
     * can't re-enable a button mid add-to-cart). Warmed on hover/focus, so
     * it's usually ready before the flying image lands.
     */
    function prepareCutout(entry) {
      if (!entry || !entry.sourceImage) return;
      const cached = getCutout(entry.sourceImage);
      if (cached.value) {
        entry.image = cached.value;
        return;
      }
      cached.promise.then((result) => {
        if (entry.image === result) return;
        entry.image = result;
        paintAllSlots();
      });
    }

    const warmCutout = (event) => {
      const card = event.target.closest && event.target.closest('[data-bb-card]');
      if (card) prepareCutout(state.get(card.dataset.variantId));
    };
    grid.addEventListener('pointerover', warmCutout);
    grid.addEventListener('focusin', warmCutout);

    function totalCount() {
      return unitOrder.length;
    }

    function buildSlotUnits() {
      return unitOrder.map((id) => state.get(id)).filter(Boolean);
    }

    function distinctPickOrder() {
      const seen = new Set();
      const order = [];
      unitOrder.forEach((id) => {
        if (!seen.has(id)) {
          seen.add(id);
          order.push(id);
        }
      });
      return order;
    }

    function template(str, tokens) {
      return str.replace(/%(\w+)%/g, (match, key) => (key in tokens ? tokens[key] : match));
    }

    /** Highest tier reached at `count`, or null. */
    function tierFor(count) {
      let reached = null;
      tiers.forEach((tier) => {
        if (count >= tier.qty) reached = tier;
      });
      return reached;
    }

    /** Next tier not yet reached at `count` (and reachable within max), or null. */
    function nextTierFor(count) {
      return tiers.find((tier) => count < tier.qty && tier.qty <= max) || null;
    }

    /** The plan this entry would subscribe on, or null (one-time). */
    function planFor(entry) {
      if (purchase.mode !== 'subscribe' || !purchase.interval) return null;
      const plan = entry.plans[purchase.interval];
      return plan ? { id: String(plan[0]), price: parseInt(plan[1], 10) || entry.price } : null;
    }

    /** Price per unit at the current purchase option (subscription price if subscribing). */
    function unitPrice(entry) {
      const plan = planFor(entry);
      return plan ? plan.price : entry.price;
    }

    // ---- Purchase option: one-time vs subscribe, and the interval ----
    // Interval options are every plan name found across the products, in
    // first-seen order (e.g. Every 30 / 60 / 90 Days).
    const intervals = [];
    state.forEach((entry) => {
      Object.keys(entry.plans).forEach((name) => {
        if (!intervals.includes(name)) intervals.push(name);
      });
    });
    purchase.interval = intervals[0] || '';
    if (!intervals.length) purchase.mode = 'onetime';

    intervalSelects.forEach((select) => {
      intervals.forEach((name) => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        select.appendChild(option);
      });
    });
    // Each rendered copy (sidebar, desktop panel) is its own radio group.
    purchaseGroups.forEach((group, i) => {
      group.querySelectorAll('[data-bb-purchase-input]').forEach((input) => {
        input.name = 'bb-purchase-' + (root.id || 'bundle-' + instanceIndex) + '-' + i;
      });
      if (!intervals.length) group.hidden = true;
    });

    function renderPurchase() {
      const subscribing = purchase.mode === 'subscribe';
      purchaseInputs.forEach((input) => {
        input.checked = input.value === purchase.mode;
      });
      purchaseOptions.forEach((option) => {
        option.classList.toggle('is-selected', option.dataset.bbPurchaseOption === purchase.mode);
      });
      intervalWraps.forEach((wrap) => {
        wrap.hidden = !subscribing;
      });
      intervalSelects.forEach((select) => {
        select.value = purchase.interval;
      });
    }

    purchaseInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (addToCartInFlight) {
          renderPurchase();
          return;
        }
        if (!input.checked) return;
        purchase.mode = input.value === 'subscribe' ? 'subscribe' : 'onetime';
        renderPurchase();
        renderSidebar();
      });
    });
    intervalSelects.forEach((select) => {
      select.addEventListener('change', () => {
        if (addToCartInFlight) {
          renderPurchase();
          return;
        }
        purchase.interval = select.value;
        renderPurchase();
        renderSidebar();
      });
    });

    function isMobileViewport() {
      try {
        return window.matchMedia('(max-width: 749px)').matches;
      } catch (e) {
        return false;
      }
    }

    function setCtaLabel(text) {
      if (ctaLabel) ctaLabel.textContent = text;
      if (mobileCtaLabel) mobileCtaLabel.textContent = text;
      if (desktopCtaLabel) desktopCtaLabel.textContent = text;
    }

    function setCtaDisabled(disabled) {
      ctaBtn.disabled = disabled;
      if (mobileCtaBtn) mobileCtaBtn.disabled = disabled;
      if (desktopCtaBtn) desktopCtaBtn.disabled = disabled;
    }

    function setAddStatus(text) {
      if (addStatusEl) addStatusEl.textContent = text;
      if (desktopAddStatusEl) desktopAddStatusEl.textContent = text;
    }

    /**
     * Card image flies into its destination slot (upright, no rotation;
     * filled slots here aren't rotated either), then `done()` commits the add.
     */
    function flyClone(srcImgEl, destEl, done, cutoutSrc) {
      const s = srcImgEl && typeof srcImgEl.getBoundingClientRect === 'function' ? srcImgEl.getBoundingClientRect() : null;
      const d = destEl && typeof destEl.getBoundingClientRect === 'function' ? destEl.getBoundingClientRect() : null;

      if (!s || !d || !s.width || !s.height || !d.width || typeof srcImgEl.animate !== 'function') {
        done();
        return;
      }

      const clone = srcImgEl.cloneNode(true);
      if (cutoutSrc) {
        // Background-removed copy, so no white box flies over the sidebar.
        clone.removeAttribute('srcset');
        clone.removeAttribute('sizes');
        clone.src = cutoutSrc;
        clone.style.objectFit = 'contain';
      }
      const cx = s.left + s.width / 2;
      const cy = s.top + s.height / 2;
      const dx = d.left + d.width / 2 - cx;
      const dy = d.top + d.height / 2 - cy;
      // Fits the upright image inside the slot (the slot draws it with `contain`).
      const endScale = Math.max(0.12, Math.min(d.width / s.width, d.height / s.height));

      Object.assign(clone.style, {
        position: 'fixed',
        left: s.left + 'px',
        top: s.top + 'px',
        width: s.width + 'px',
        height: s.height + 'px',
        margin: '0',
        zIndex: '9999',
        pointerEvents: 'none',
        maxWidth: 'none',
        filter: 'drop-shadow(0 18px 26px rgba(0, 0, 0, 0.45))',
        transition: 'none',
        mixBlendMode: 'normal',
      });
      document.body.appendChild(clone);

      const anim = clone.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
          {
            transform:
              'translate(' +
              dx * 0.5 +
              'px, ' +
              (dy * 0.5 - 40) +
              'px) scale(' +
              (1 + endScale) / 2 +
              ')',
            opacity: 1,
            offset: 0.55,
          },
          {
            transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(' + endScale + ')',
            opacity: 0.85,
            offset: 1,
          },
        ],
        { duration: 560, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
      );

      const cleanup = () => {
        clone.remove();
        done();
      };
      anim.onfinish = cleanup;
      anim.oncancel = cleanup;
    }

    function nextDestinationIndex() {
      return totalCount() + totalPending();
    }

    function pulseSidebar() {
      const sidebar = root.querySelector('[data-bb-sidebar]');
      if (!sidebar || typeof sidebar.animate !== 'function') return;
      sidebar.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(-7px)' },
          { transform: 'translateX(7px)' },
          { transform: 'translateX(-4px)' },
          { transform: 'translateX(0)' },
        ],
        { duration: 320, easing: 'ease-in-out' }
      );
    }

    function pulseCta() {
      if (typeof ctaBtn.animate !== 'function') return;
      ctaBtn.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }],
        { duration: 360, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    }

    function animateGrid() {
      if (!grid || typeof grid.animate !== 'function') return;
      grid.animate(
        [
          { opacity: 0, transform: 'translateY(12px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    }

    function setQty(id, qty) {
      const entry = state.get(id);
      if (!entry) return;

      const nextQty = Math.max(0, qty);
      const delta = nextQty - entry.qty;

      if (delta > 0 && totalCount() + delta > itemLimit) return;

      if (delta > 0) {
        for (let i = 0; i < delta; i++) unitOrder.push(id);
      } else if (delta < 0) {
        for (let i = 0; i < -delta; i++) {
          const idx = unitOrder.lastIndexOf(id);
          if (idx !== -1) unitOrder.splice(idx, 1);
        }
      }

      entry.qty = nextQty;

      // Every card's add/+ disabled state depends on the total, so a change
      // here re-renders all of them (cheap, and keeps them in sync at max).
      cards.forEach(renderCard);
      renderSidebar();
    }

    /**
     * The add button/stepper reflect the SELECTED variant's quantity; the
     * corner badge shows the product's total across all its variants.
     */
    function renderCard(card) {
      const entry = state.get(card.dataset.variantId);
      if (!entry) return;
      const productQty = (entriesByCard.get(card) || []).reduce((sum, e) => sum + e.qty, 0);
      const addBtn = card.querySelector('[data-bb-add]');
      const stepper = card.querySelector('[data-bb-stepper]');
      const qtyEl = card.querySelector('[data-bb-qty]');
      const badge = card.querySelector('[data-bb-card-badge]');
      const increaseBtn = card.querySelector('[data-bb-increase]');

      const hasQty = entry.qty > 0;
      const isFull = totalCount() >= itemLimit;

      if (addBtn) {
        addBtn.hidden = hasQty;
        addBtn.disabled = isFull;
      }
      if (stepper) stepper.hidden = !hasQty;
      if (qtyEl) qtyEl.textContent = String(entry.qty);
      if (badge) {
        badge.hidden = productQty < 1;
        badge.textContent = String(productQty);
      }
      if (increaseBtn) increaseBtn.disabled = isFull;
    }

    /**
     * Keeps the number of slots right for `used` units (filled + in flight):
     * the bundle size plus the bonus slots, or one past the last used slot,
     * whichever is more, never past the item limit. Slots past the bundle
     * size get the --extra class (dashed outline while empty).
     */
    function syncSlotCount(used) {
      const wanted = allowMore ? Math.min(itemLimit, Math.max(max + extraSlots, used + 1)) : max;
      [
        [slotsWrap, 'data-bb-slot'],
        [desktopSlotsWrap, 'data-bb-desktop-slot'],
      ].forEach(([wrap, attr]) => {
        if (!wrap) return;
        let current = wrap.querySelectorAll('[' + attr + ']');
        for (let i = current.length; i < wanted; i++) {
          const slot = document.createElement('span');
          slot.className = 'bundle-builder__slot';
          slot.setAttribute(attr, '');
          wrap.appendChild(slot);
        }
        current = Array.from(wrap.querySelectorAll('[' + attr + ']'));
        current.slice(wanted).forEach((slot) => slot.remove());
        current.slice(0, wanted).forEach((slot, i) => slot.classList.toggle('bundle-builder__slot--extra', i >= max));
      });
      slots = Array.from(root.querySelectorAll('[data-bb-slot]'));
      desktopSlots = Array.from(root.querySelectorAll('[data-bb-desktop-slot]'));
    }

    /** Fills slots in selection order, in the sidebar and the desktop panel. */
    function paintAllSlots() {
      const units = buildSlotUnits();
      syncSlotCount(units.length + totalPending());
      [slots, desktopSlots].forEach((slotEls) => {
        slotEls.forEach((slot, i) => {
          const unit = units[i];
          if (unit) {
            slot.classList.add('is-filled');
            if (unit.image) {
              slot.style.setProperty('--bb-slot-image', 'url("' + unit.image + '")');
              slot.style.removeProperty('--bb-slot-fallback-color');
            } else {
              slot.style.removeProperty('--bb-slot-image');
              slot.style.setProperty('--bb-slot-fallback-color', unit.color);
            }
          } else {
            slot.classList.remove('is-filled');
            slot.style.removeProperty('--bb-slot-image');
            slot.style.removeProperty('--bb-slot-fallback-color');
          }
        });
      });
    }

    function renderSidebar() {
      const count = totalCount();
      const remaining = Math.max(0, min - count);
      const isReady = count >= min;
      const isFull = count >= max;
      const isOver = count > max;
      const atLimit = count >= itemLimit;
      const offset = Math.max(0, PROGRESS_CIRCUMFERENCE * (1 - Math.min(1, count / max)));
      const ringText = isOver ? max + '+' : String(count);

      let countText = template(labels.countProgress, { count, max });
      if (isOver) countText = template(labels.countOver, { count });
      else if (isFull) countText = template(labels.countComplete, { max });
      const statusText = isReady ? labels.statusReady : labels.statusBuilding;

      // Pricing + tier messaging.
      //  fullSubtotal: one-time prices
      //  baseSubtotal: at the current purchase option (subscription prices
      //                where the item has a plan for the chosen interval)
      //  total:        baseSubtotal less the bundle tier discount, when it
      //                applies (one-time always; subscriptions per setting)
      const subscribing = purchase.mode === 'subscribe';
      const tier = tierFor(count);
      const nextTier = nextTierFor(count);

      // Per line, so the tier is only applied where checkout will apply it:
      // one-time lines always, subscription lines only per setting (an item
      // with no plan falls back to one-time even while subscribing).
      const lines = distinctPickOrder()
        .map((id) => state.get(id))
        .filter((entry) => entry && entry.qty > 0)
        .map((entry) => ({
          entry,
          full: entry.price * entry.qty,
          base: unitPrice(entry) * entry.qty,
          tiered: !!tier && (!planFor(entry) || tierOnSubscriptions),
        }));
      const fullSubtotal = lines.reduce((sum, line) => sum + line.full, 0);
      const baseSubtotal = lines.reduce((sum, line) => sum + line.base, 0);
      const tieredBase = lines.reduce((sum, line) => sum + (line.tiered ? line.base : 0), 0);
      // Any fraction of a cent rounds the total UP to the next cent (e.g.
      // $82.1325 shows $82.14): the discount rounds down, line prices round
      // up. The 1e-9 tolerance keeps float noise (950.0000001) from
      // bumping an exact amount up a cent.
      const tierSavings = tier ? Math.floor((tieredBase * tier.percent) / 100 + 1e-9) : 0;
      const total = baseSubtotal - tierSavings;
      const subscribeSavings = fullSubtotal - baseSubtotal;

      const shippingThreshold = subscribing ? freeShipping.subscriptionThreshold : freeShipping.threshold;
      const shippingActive = freeShipping.enabled && shippingThreshold > 0;
      const shippingRemaining = shippingActive ? Math.max(0, shippingThreshold - (freeShipping.cartTotal + total)) : 0;
      if (shippingActive) {
        const progress = Math.min(1, (freeShipping.cartTotal + total) / shippingThreshold);
        const unlocked = shippingRemaining === 0;
        const shippingText = unlocked
          ? labels.shippingUnlocked
          : template(labels.shippingAway, { amount: formatMoney(shippingRemaining, moneyFormat) });
        shippingEls.forEach((el) => el.classList.toggle('is-unlocked', unlocked));
        shippingTextEls.forEach((el) => {
          el.textContent = shippingText;
        });
        shippingFillEls.forEach((el) => {
          el.style.width = (progress * 100).toFixed(2) + '%';
        });
      }

      // Line prices: each line's exact discounted price rounded down, then
      // the cents left over (to reach the rounded-up total) go one each to
      // the lines with the largest fractions. Every line stays within a cent
      // of its exact price and the lines always add up to the total.
      const lineTotals = new Map();
      const exactLines = lines.map((line) => {
        const exact = line.tiered ? (line.base * (100 - tier.percent)) / 100 : line.base;
        const floored = Math.floor(exact + 1e-9);
        return { id: line.entry.id, floored, fraction: exact - floored };
      });
      let leftover = total - exactLines.reduce((sum, line) => sum + line.floored, 0);
      exactLines
        .slice()
        .sort((a, b) => b.fraction - a.fraction)
        .forEach((line) => {
          const extra = leftover > 0 ? 1 : 0;
          leftover -= extra;
          lineTotals.set(line.id, line.floored + extra);
        });
      const totalText = formatMoney(total, moneyFormat);
      const compareText = total < fullSubtotal ? formatMoney(fullSubtotal, moneyFormat) : '';

      let tierText = '';
      if (nextTier) {
        tierText = template(labels.tierNext, { needed: nextTier.qty - count, percent: nextTier.percent });
      } else if (tier) {
        tierText = template(labels.tierUnlocked, { percent: tier.percent });
      }

      [countEl, mobileCountEl, desktopCountEl, desktopToggleCountEl].forEach((el) => {
        if (el) el.textContent = ringText;
      });
      // "6+" replaces the whole "count of max" on the larger rings.
      progressOfEls.forEach((el) => {
        el.hidden = isOver;
      });
      [desktopFractionEl].forEach((el) => {
        if (el) el.textContent = isOver ? ringText : count + '/' + max;
      });
      [progressRing, mobileProgressRing, desktopProgressRing, desktopToggleRing].forEach((ring) => {
        if (ring) ring.style.strokeDashoffset = String(offset);
      });
      [statusEl, desktopStatusEl].forEach((el) => {
        if (el) el.textContent = statusText;
      });
      [countLabelEl, desktopCountLabelEl, desktopToggleCountLabelEl].forEach((el) => {
        if (el) el.textContent = countText;
      });
      // Collapsed mobile bar: short copy so the savings read at a glance.
      // Top line = progress (+ current discount); bold line = next goal.
      if (mobileSummaryEl) {
        if (isOver) {
          mobileSummaryEl.textContent = tier
            ? template(labels.mobileSummaryOver, { count, percent: tier.percent })
            : count + ' Items';
        } else {
          mobileSummaryEl.textContent = tier
            ? template(labels.mobileSummaryDiscount, { count, max, percent: tier.percent })
            : template(labels.mobileSummary, { count, max });
        }
      }
      if (mobileCountLabelEl) {
        let mobileText = countText;
        if (nextTier) {
          mobileText = template(labels.mobileTierNext, { needed: nextTier.qty - count, percent: nextTier.percent });
        } else if (tier && shippingActive) {
          // Top discount reached: the next goal is free shipping.
          mobileText =
            shippingRemaining > 0
              ? template(labels.mobileShippingAway, { amount: formatMoney(shippingRemaining, moneyFormat) })
              : template(labels.mobileShippingUnlocked, { percent: tier.percent });
        } else if (tier) {
          mobileText = template(labels.mobileTierUnlocked, { percent: tier.percent });
        }
        mobileCountLabelEl.textContent = mobileText;
      }
      [tierLabelEl, desktopTierLabelEl].forEach((el) => {
        if (!el) return;
        el.textContent = tierText;
      });
      const hasDiscount = !!compareText;
      totalPriceEls.forEach((el) => {
        el.textContent = totalText;
      });
      comparePriceEls.forEach((el) => {
        el.textContent = compareText;
        el.hidden = !hasDiscount;
      });
      subtotalRows.forEach((el) => {
        el.hidden = !hasDiscount;
      });
      subtotalEls.forEach((el) => {
        el.textContent = formatMoney(fullSubtotal, moneyFormat);
      });
      subscribeRows.forEach((el) => {
        el.hidden = subscribeSavings <= 0;
      });
      subscribeAmountEls.forEach((el) => {
        el.textContent = '−' + formatMoney(subscribeSavings, moneyFormat);
      });
      discountRows.forEach((el) => {
        el.hidden = tierSavings <= 0;
      });
      discountPercentEls.forEach((el) => {
        el.textContent = tier ? String(tier.percent) : '';
      });
      discountAmountEls.forEach((el) => {
        el.textContent = '−' + formatMoney(tierSavings, moneyFormat);
      });
      tierPills.forEach((pill) => {
        const reached = count >= (parseInt(pill.dataset.tierQty, 10) || 0);
        pill.classList.toggle('is-reached', reached);
        pill.classList.toggle('is-current', !!tier && tier.qty === parseInt(pill.dataset.tierQty, 10));
      });

      paintAllSlots();

      function renderSelectedListInto(listEl) {
        if (!listEl) return;
        listEl.textContent = '';
        distinctPickOrder().forEach((id) => {
          const entry = state.get(id);
          if (!entry || entry.qty < 1) return;

          const li = document.createElement('li');
          li.className = 'bundle-builder__selected-item';
          li.innerHTML =
            '<span class="bundle-builder__selected-item-dot" aria-hidden="true"></span>' +
            '<span class="bundle-builder__selected-item-info">' +
            '<span class="bundle-builder__selected-item-title"></span>' +
            '<span class="bundle-builder__selected-item-category"></span>' +
            '</span>' +
            '<span class="bundle-builder__selected-item-price">' +
            '<s class="bundle-builder__selected-item-compare"></s>' +
            '<span class="bundle-builder__selected-item-total"></span>' +
            '</span>' +
            '<div class="bundle-builder__stepper">' +
            '<button type="button" class="bundle-builder__stepper-btn" data-bb-decrease>−</button>' +
            '<span class="bundle-builder__stepper-qty" data-bb-qty></span>' +
            '<button type="button" class="bundle-builder__stepper-btn bundle-builder__stepper-btn--accent" data-bb-increase>+</button>' +
            '</div>';

          li.style.setProperty('--bb-item-color', entry.color);
          li.dataset.id = entry.id;
          li.querySelector('.bundle-builder__selected-item-title').textContent = entry.title;
          const categoryEl = li.querySelector('.bundle-builder__selected-item-category');
          const itemNote = [entry.variantTitle];
          if (subscribing && !planFor(entry)) itemNote.push(onetimeOnlyLabel);
          categoryEl.textContent = itemNote.filter(Boolean).join(' · ');
          categoryEl.hidden = !categoryEl.textContent;
          const lineCents = entry.price * entry.qty;
          const lineTotal = lineTotals.get(entry.id);
          const lineCompare = li.querySelector('.bundle-builder__selected-item-compare');
          lineCompare.textContent = lineTotal < lineCents ? formatMoney(lineCents, moneyFormat) : '';
          lineCompare.hidden = !(lineTotal < lineCents);
          li.querySelector('.bundle-builder__selected-item-total').textContent = formatMoney(lineTotal, moneyFormat);
          const fullTitle = entry.variantTitle ? entry.title + ' (' + entry.variantTitle + ')' : entry.title;
          li.querySelector('[data-bb-decrease]').setAttribute('aria-label', 'Remove one ' + fullTitle);
          const increase = li.querySelector('[data-bb-increase]');
          increase.setAttribute('aria-label', 'Add one more ' + fullTitle);
          increase.disabled = atLimit;
          li.querySelector('[data-bb-qty]').textContent = String(entry.qty);

          listEl.appendChild(li);
        });
      }
      renderSelectedListInto(selectedList);
      renderSelectedListInto(desktopSelectedList);

      saveState();
      // The list may have grown or shrunk, changing what's below the fold.
      if (typeof updateScrollHint === 'function') requestAnimationFrame(updateScrollHint);

      setCtaDisabled(!isReady);
      setCtaLabel(isReady ? template(labels.add, { price: totalText }) : template(labels.addIncomplete, { remaining }));
    }

    function clearAll() {
      cancelPendingFlights();
      // Empty `unitOrder` first; renderCard() reads the live total.
      unitOrder.length = 0;
      state.forEach((entry) => {
        entry.qty = 0;
      });
      cards.forEach(renderCard);
      renderSidebar();
      setAddStatus('');
    }

    /** Pulses the CTA when an add crosses the minimum or any tier threshold. */
    function pulseIfThresholdCrossed(before, after) {
      const thresholds = [min].concat(tiers.map((tier) => tier.qty));
      if (thresholds.some((qty) => before < qty && after >= qty)) pulseCta();
    }

    // ---- Remembering the bundle in progress ----
    // Product images/titles link to their product pages in the same tab, so
    // the bundle (units in pick order, each card's selected variant, and the
    // purchase option) is kept in sessionStorage and restored on return.
    // Anything no longer offered (e.g. sold out since) is skipped. Cleared
    // once the bundle is added to cart. Storage can be unavailable (private
    // mode, blocked site data), so every access is guarded.
    const storageKey = 'bb-products:' + window.location.pathname + ':' + (root.id || instanceIndex);
    let restoring = false;

    function saveState() {
      if (restoring) return;
      try {
        if (!unitOrder.length) {
          window.sessionStorage.removeItem(storageKey);
          return;
        }
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            units: unitOrder,
            selected: cards.map((card) => card.dataset.variantId),
            mode: purchase.mode,
            interval: purchase.interval,
          })
        );
      } catch (e) {
        // Storage unavailable: the builder just won't remember.
      }
    }

    function clearSavedState() {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (e) {
        // ignore
      }
    }

    function restoreState() {
      if (window.Shopify && window.Shopify.designMode) return;
      let saved = null;
      try {
        saved = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
      } catch (e) {
        return;
      }
      if (!saved || typeof saved !== 'object') return;

      restoring = true;
      // Each card's dropdown back to the variant that was showing.
      if (Array.isArray(saved.selected)) {
        cards.forEach((card, i) => {
          const select = card.querySelector('[data-bb-variant-select]');
          const id = saved.selected[i];
          if (!select || !id) return;
          const option = Array.from(select.options).find((o) => o.value === String(id));
          if (!option) return;
          select.value = option.value;
          syncCardToVariant(card, option);
        });
      }
      // Units in their original pick order, only for variants still offered.
      if (Array.isArray(saved.units)) {
        saved.units.forEach((id) => {
          const entry = state.get(String(id));
          if (!entry || unitOrder.length >= itemLimit) return;
          unitOrder.push(entry.id);
          entry.qty += 1;
        });
      }
      if (saved.mode === 'subscribe' && intervals.length) purchase.mode = 'subscribe';
      if (saved.mode === 'onetime') purchase.mode = 'onetime';
      if (saved.interval && intervals.includes(saved.interval)) purchase.interval = saved.interval;
      restoring = false;

      state.forEach((entry) => {
        if (entry.qty > 0) prepareCutout(entry);
      });
      cards.forEach(renderCard);
    }

    // ---- Card grid: variant dropdown ----
    // Switches which variant the card's add/stepper act on, and updates the
    // card's price, photo, flavor badge and dot color to that variant's.
    function syncCardToVariant(card, option) {
      card.dataset.variantId = option.value;

      const priceEl = card.querySelector('[data-bb-card-price]');
      if (priceEl && option.dataset.priceText) priceEl.textContent = option.dataset.priceText;

      const photo = card.querySelector('.bundle-builder__card-image img:not(.bundle-builder__card-mountain)');
      if (photo && option.dataset.cardSrc && photo.getAttribute('src') !== option.dataset.cardSrc) {
        photo.srcset = option.dataset.cardSrcset || '';
        photo.src = option.dataset.cardSrc;
      }

      const flavorBadge = card.querySelector('.bundle-builder__card-flavor-badge');
      if (flavorBadge) {
        if (option.dataset.badge) {
          flavorBadge.removeAttribute('srcset');
          flavorBadge.src = option.dataset.badge;
          flavorBadge.hidden = false;
        } else {
          flavorBadge.hidden = true;
        }
      }

      if (option.dataset.color) card.style.setProperty('--bb-flavor-color', option.dataset.color);

      if (option.dataset.url) {
        card.querySelectorAll('[data-bb-product-link]').forEach((link) => {
          link.href = option.dataset.url;
        });
      }

      const title = card.dataset.title + (option.dataset.title ? ' (' + option.dataset.title + ')' : '');
      const decrease = card.querySelector('[data-bb-decrease]');
      const increase = card.querySelector('[data-bb-increase]');
      if (decrease) decrease.setAttribute('aria-label', 'Remove one ' + title);
      if (increase) increase.setAttribute('aria-label', 'Add one more ' + title);
    }

    grid.addEventListener('change', (event) => {
      const select = event.target.closest('[data-bb-variant-select]');
      if (!select) return;
      const card = select.closest('[data-bb-card]');
      const option = select.selectedOptions[0];
      if (!card || !option) return;

      syncCardToVariant(card, option);
      renderCard(card);
      prepareCutout(state.get(option.value));
    });

    // A browser can restore a previously chosen option on reload/back, so the
    // card is synced to whatever the dropdown actually shows at load.
    cards.forEach((card) => {
      const select = card.querySelector('[data-bb-variant-select]');
      if (select && select.selectedOptions[0]) syncCardToVariant(card, select.selectedOptions[0]);
    });

    /**
     * Adds one unit of `entry`, flying `srcImgEl` (if given, desktop only)
     * into its slot first. Shared by the card grid and the public API
     * (e.g. the Bundle Packs section's "Add all to bundle"). The unit is
     * reserved immediately (counted in totalPending()); `delay` only
     * staggers when its animation starts. Returns false when the bundle is
     * already at its item limit.
     */
    function addUnit(entry, srcImgEl, delay) {
      const id = entry.id;
      if (totalCount() + totalPending() >= itemLimit) {
        pulseSidebar();
        pulseCta();
        return false;
      }

      const destIndex = nextDestinationIndex();
      // Make sure the destination slot exists before flying to it.
      syncSlotCount(destIndex + 1);
      const destEl = slots[Math.min(destIndex, slots.length - 1)] || null;

      const token = { cancelled: false };
      let list = pendingFlightsById.get(id);
      if (!list) {
        list = [];
        pendingFlightsById.set(id, list);
      }
      list.push(token);

      const settle = () => {
        const idx = list.indexOf(token);
        if (idx !== -1) list.splice(idx, 1);
        if (list.length === 0) pendingFlightsById.delete(id);

        if (token.cancelled) return;

        const before = totalCount();
        setQty(id, entry.qty + 1);
        pulseIfThresholdCrossed(before, totalCount());
      };

      prepareCutout(entry);
      const cutout = entry.image !== entry.sourceImage ? entry.image : null;

      const start = () => {
        if (token.cancelled) {
          settle();
          return;
        }
        if (srcImgEl && destEl && !isMobileViewport()) {
          flyClone(srcImgEl, destEl, settle, cutout);
        } else {
          settle();
        }
      };
      if (delay > 0) {
        setTimeout(start, delay);
      } else {
        start();
      }
      return true;
    }

    /** Cancels every add still in flight or queued (Clear, add to cart). */
    function cancelPendingFlights() {
      pendingFlightsById.forEach((list) => {
        list.forEach((token) => {
          token.cancelled = true;
        });
      });
    }

    // ---- Card grid: add / stepper delegation ----
    grid.addEventListener('click', (event) => {
      if (addToCartInFlight) return;
      const card = event.target.closest('[data-bb-card]');
      if (!card) return;
      const id = card.dataset.variantId;
      const entry = state.get(id);

      if (event.target.closest('[data-bb-add]') || event.target.closest('[data-bb-increase]')) {
        if (!entry) return;
        addUnit(entry, card.querySelector('.bundle-builder__card-image img:not(.bundle-builder__card-mountain)'));
      } else if (event.target.closest('[data-bb-decrease]')) {
        const pending = pendingFlightsById.get(id);
        if (pending && pending.length) pending[pending.length - 1].cancelled = true;

        if (entry) setQty(id, entry.qty - 1);
      }
    });

    // ---- Selected list(s): stepper delegation ----
    function wireSelectedListSteppers(listEl) {
      if (!listEl) return;
      listEl.addEventListener('click', (event) => {
        if (addToCartInFlight) return;
        const row = event.target.closest('[data-id]');
        if (!row) return;
        const id = row.dataset.id;
        const entry = state.get(id);
        if (!entry) return;

        if (event.target.closest('[data-bb-increase]')) {
          if (totalCount() + totalPending() >= itemLimit) {
            pulseSidebar();
            pulseCta();
            return;
          }
          const before = totalCount();
          setQty(id, entry.qty + 1);
          pulseIfThresholdCrossed(before, totalCount());
        } else if (event.target.closest('[data-bb-decrease]')) {
          const pending = pendingFlightsById.get(id);
          if (pending && pending.length) pending[pending.length - 1].cancelled = true;

          setQty(id, entry.qty - 1);
        }
      });
    }
    wireSelectedListSteppers(selectedList);
    wireSelectedListSteppers(desktopSelectedList);

    // ---- Tabs: filter grid ----
    if (tabsWrap) {
      tabsWrap.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-bb-tab], [data-bb-all-tab]');
        if (!tab) return;

        tabsWrap.querySelectorAll('[data-bb-tab], [data-bb-all-tab]').forEach((t) => {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });

        const isAllTab = tab.hasAttribute('data-bb-all-tab');
        const tag = tab.dataset.bbTab;
        cards.forEach((card) => {
          const categories = (card.dataset.categories || '').split(',').filter(Boolean);
          card.hidden = !(isAllTab || categories.includes(tag));
        });

        animateGrid();
      });
    }

    // ---- Clear ----
    const handleClear = () => {
      if (addToCartInFlight) return;
      clearAll();
    };
    if (clearBtn) clearBtn.addEventListener('click', handleClear);
    if (desktopClearBtn) desktopClearBtn.addEventListener('click', handleClear);

    // ---- Mobile bar: expand/collapse the sidebar sheet, forward the CTA ----
    const sidebarEl = root.querySelector('[data-bb-sidebar]');
    if (mobileToggle && sidebarEl) {
      mobileToggle.addEventListener('click', () => {
        const expanded = sidebarEl.hasAttribute('data-bb-expanded');
        if (expanded) {
          sidebarEl.removeAttribute('data-bb-expanded');
        } else {
          sidebarEl.setAttribute('data-bb-expanded', '');
        }
        mobileToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (mobileChevron) mobileChevron.textContent = expanded ? '▲' : '▼';
      });
    }
    if (mobileCtaBtn) mobileCtaBtn.addEventListener('click', () => ctaBtn.click());

    // ---- Mobile summary sheet: "Scroll for your savings" hint ----
    // Shown while the expanded sheet has more content below the fold;
    // hidden at the end of the sheet or when everything fits. Tapping it
    // scrolls to the savings breakdown.
    const scrollHint = root.querySelector('[data-bb-scroll-hint]');
    function updateScrollHint() {
      if (!scrollHint || !sidebarEl) return;
      const expanded = sidebarEl.hasAttribute('data-bb-expanded');
      const atEnd = sidebarEl.scrollTop + sidebarEl.clientHeight >= sidebarEl.scrollHeight - 24;
      scrollHint.classList.toggle('is-hidden', !expanded || atEnd);
    }
    if (scrollHint && sidebarEl) {
      sidebarEl.addEventListener('scroll', updateScrollHint, { passive: true });
      window.addEventListener('resize', updateScrollHint);
      // The sheet animates open (max-height transition), so check again
      // once it has its final size.
      sidebarEl.addEventListener('transitionend', updateScrollHint);
      if (mobileToggle) mobileToggle.addEventListener('click', () => setTimeout(updateScrollHint, 350));
      scrollHint.addEventListener('click', () => {
        const target = sidebarEl.querySelector('.bundle-builder__purchase, .bundle-builder__summary');
        const top = target
          ? target.getBoundingClientRect().top - sidebarEl.getBoundingClientRect().top + sidebarEl.scrollTop - 12
          : sidebarEl.scrollHeight;
        sidebarEl.scrollTo({ top, behavior: 'smooth' });
      });
    }

    // ---- Mobile bar: optionally confine it to this section only ----
    if (mobileDock && root.dataset.limitMobileDock === 'true' && 'IntersectionObserver' in window) {
      mobileDock.setAttribute('data-bb-outside-section', '');
      const sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              mobileDock.removeAttribute('data-bb-outside-section');
            } else {
              mobileDock.setAttribute('data-bb-outside-section', '');
            }
          });
        },
        { threshold: 0 }
      );
      sectionObserver.observe(root);
    }

    // ---- Desktop bubble: expand/collapse the floating panel, forward the CTA ----
    if (desktopToggle && desktopPanel) {
      desktopToggle.addEventListener('click', () => {
        const expanded = desktopPanel.hasAttribute('data-bb-desktop-expanded');
        if (expanded) {
          desktopPanel.removeAttribute('data-bb-desktop-expanded');
        } else {
          desktopPanel.setAttribute('data-bb-desktop-expanded', '');
        }
        desktopToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (desktopChevron) desktopChevron.textContent = expanded ? '▲' : '▼';
      });
    }
    if (desktopCtaBtn) desktopCtaBtn.addEventListener('click', () => ctaBtn.click());

    // ---- Desktop bubble: optionally hide it until the section is scrolled past ----
    // On by default. While the section is on screen its own sidebar is
    // visible, so the bubble would just duplicate it; before the visitor
    // reaches the section it isn't relevant yet. It shows only once the
    // section's bottom edge has scrolled above the viewport.
    const desktopBubble = root.querySelector('[data-bb-desktop-bubble]');
    if (desktopBubble && root.dataset.bubbleAfterSection !== 'false' && 'IntersectionObserver' in window) {
      const hideBubble = () => {
        if (desktopBubble.hasAttribute('data-bb-before-section-end')) return;
        desktopBubble.setAttribute('data-bb-before-section-end', '');
        // Collapse an open panel so it doesn't reappear open over the section.
        if (desktopPanel && desktopPanel.hasAttribute('data-bb-desktop-expanded')) {
          desktopPanel.removeAttribute('data-bb-desktop-expanded');
          if (desktopToggle) desktopToggle.setAttribute('aria-expanded', 'false');
          if (desktopChevron) desktopChevron.textContent = '▲';
        }
      };

      // Hidden before the observer's first callback, so it never flashes in.
      hideBubble();

      const bubbleObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
            if (scrolledPast) {
              desktopBubble.removeAttribute('data-bb-before-section-end');
            } else {
              hideBubble();
            }
          });
        },
        { threshold: 0 }
      );
      bubbleObserver.observe(root);
    }

    function themeRoute(key, fallback) {
      return typeof Theme !== 'undefined' && Theme.routes && Theme.routes[key] ? Theme.routes[key] : fallback;
    }

    function cartSectionIds() {
      const ids = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el.dataset && el.dataset.sectionId) ids.push(el.dataset.sectionId);
      });
      return ids;
    }

    /**
     * Applies the right tier code for what's now in the cart. The tier is
     * picked from the cart's TOTAL quantity of this builder's products
     * (not just this pack), matching how a Shopify code with a "minimum
     * quantity" requirement counts, so two separate 3-item packs earn the
     * 6-item code. Only tiers with a code count. Keeps every other code on
     * the cart and swaps out this section's other tier codes. Returns the
     * /cart/update.js response (with rendered sections), or null when
     * there's nothing to change.
     */
    async function applyTierCode(sectionIds) {
      if (!tierCodes.length) return null;

      // An empty update changes nothing and returns the cart, including
      // `items` and `discount_codes` (the same shape cart-discount.js reads).
      const updateUrl = themeRoute('cart_update_url', '/cart/update.js');
      const cartResponse = await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({}),
      });
      const cart = await cartResponse.json();

      const eligibleQty = (cart.items || []).reduce(
        (sum, item) => (state.has(String(item.variant_id)) ? sum + item.quantity : sum),
        0
      );
      const tier = tiers.filter((t) => t.code && eligibleQty >= t.qty).pop();
      if (!tier) return null;

      const upper = (code) => code.toUpperCase();
      const existing = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
      if (existing.some((code) => upper(code) === upper(tier.code))) return null;

      const tierCodesUpper = tierCodes.map(upper);
      const kept = existing.filter((code) => !tierCodesUpper.includes(upper(code)));

      const body = { discount: kept.concat(tier.code).join(',') };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) return null;
      return response.json();
    }

    // ---- Cart total for free shipping progress ----
    // Held off while this builder's own add is in flight and until its
    // post-add reset, so the just-added bundle isn't counted twice (once in
    // the cart, once still in the builder).
    let holdCartRefresh = false;
    let cartRequest = 0;

    async function refreshCartTotal() {
      if (!freeShipping.enabled || holdCartRefresh) return;
      // Only the latest request counts, so a slow older response can't
      // overwrite a newer total.
      const requestId = ++cartRequest;
      try {
        const response = await fetch(themeRoute('cart_url', '/cart') + '.js', {
          headers: { Accept: 'application/json' },
        });
        const cart = await response.json();
        if (holdCartRefresh || requestId !== cartRequest) return;
        freeShipping.cartTotal = parseInt(cart.total_price, 10) || 0;
        renderSidebar();
      } catch (e) {
        // Cart unavailable: progress counts the bundle alone.
      }
    }

    if (freeShipping.enabled) {
      document.addEventListener('cart:update', () => {
        if (!addToCartInFlight) refreshCartTotal();
      });
      // Back/forward cache: the cart may have changed on another page.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        // Back from the cart page after a redirect-on-add: the bundle is in
        // the cart now, so the builder resets instead of counting it twice.
        if (holdCartRefresh && !addToCartInFlight) {
          clearAll();
          holdCartRefresh = false;
        }
        refreshCartTotal();
      });
    }

    // ---- Add to cart ----
    ctaBtn.addEventListener('click', async () => {
      if (ctaBtn.disabled || addToCartInFlight) return;

      const items = [];
      distinctPickOrder().forEach((id) => {
        const entry = state.get(id);
        if (!entry || entry.qty < 1) return;
        const item = { id: parseInt(entry.id, 10), quantity: entry.qty };
        // Subscribing: each item goes on its Skio plan for the chosen
        // interval. An item with no plan for it is added as one-time.
        const plan = planFor(entry);
        if (plan) item.selling_plan = Number(plan.id);
        if (showCartTag) item.properties = { Bundle: bundleLabel };
        items.push(item);
      });
      if (!items.length) return;

      const addedCount = items.reduce((sum, item) => sum + item.quantity, 0);

      const sectionIds = cartSectionIds();
      const payload = { items };
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      addToCartInFlight = true;
      holdCartRefresh = true;
      // Anything still flying in isn't in this payload; don't let it land.
      cancelPendingFlights();
      setCtaDisabled(true);
      setCtaLabel(labels.adding);
      setAddStatus('');

      try {
        const response = await fetch(themeRoute('cart_add_url', '/cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const responseData = await response.json();

        if (!response.ok || responseData.status) {
          throw new Error(responseData.description || responseData.message || 'Cart add failed');
        }

        // Items are in the cart at this point; a failed code apply shouldn't
        // read as a failed add, so it's caught separately.
        let sections = responseData.sections || {};
        try {
          const discountData = await applyTierCode(sectionIds);
          if (discountData && discountData.sections) sections = discountData.sections;
        } catch (discountError) {
          console.error(discountError);
        }

        // The bundle is in the cart now, so it's no longer "in progress".
        clearSavedState();

        setCtaLabel(labels.added);
        setAddStatus('Added to cart.');

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: 'bundle-builder-products',
              data: {
                source: 'product-form-component',
                itemCount: addedCount,
                sections,
              },
            },
          })
        );

        if (cartType === 'page') {
          window.location.href = themeRoute('cart_url', '/cart');
          return;
        }

        setTimeout(() => {
          clearAll();
          holdCartRefresh = false;
          refreshCartTotal();
        }, 1200);
      } catch (error) {
        console.error(error);
        setCtaLabel(labels.error);
        setAddStatus(error.message || labels.error);
        holdCartRefresh = false;
        setTimeout(renderSidebar, 2500);
      } finally {
        addToCartInFlight = false;
      }
    });

    // ---- Public API ----
    // A small surface for other sections on the page (e.g. Bundle Packs'
    // "Add all to bundle") to add units without reaching into this closure.
    root.bundleBuilderApi = {
      getState: function () {
        return { count: totalCount() + totalPending(), max: max, itemLimit: itemLimit };
      },
      /** True if this builder offers the variant (in stock, in a category). */
      hasVariant: function (variantId) {
        return state.has(String(variantId));
      },
      /**
       * Adds one unit per item, each `{ id: variantId, sourceEl?: img }`;
       * with a source image (desktop), each flies into its slot, staggered.
       * All-or-nothing on room: if the items wouldn't all fit under the
       * item limit, nothing is added and the summary shakes. Variants this
       * builder doesn't offer are skipped and reported in `missing`.
       */
      addVariants: function (items) {
        if (addToCartInFlight) return { ok: false, reason: 'busy', added: 0, missing: [] };
        const list = (items || []).map((item) => (typeof item === 'object' ? item : { id: item }));
        const present = list.filter((item) => state.has(String(item.id)));
        const missing = list.filter((item) => !state.has(String(item.id))).map((item) => String(item.id));
        if (!present.length) return { ok: false, reason: 'missing', added: 0, missing: missing };
        const room = itemLimit - (totalCount() + totalPending());
        if (present.length > room) {
          pulseSidebar();
          pulseCta();
          return { ok: false, reason: 'full', added: 0, missing: missing, room: Math.max(0, room) };
        }
        present.forEach((item, i) => {
          addUnit(state.get(String(item.id)), item.sourceEl || null, i * 140);
        });
        return { ok: true, added: present.length, missing: missing };
      },
    };
    restoreState();
    renderPurchase();
    renderSidebar();
    refreshCartTotal();
    root.dispatchEvent(new CustomEvent('bundle-builder:ready', { bubbles: true }));
  }

  function boot() {
    document.querySelectorAll('[data-bundle-builder-products]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
